/**
 * DOM view for the redesigned Ersatzteilefinder (2026-07 mockups).
 *
 * One DOM, two presentations switched purely by CSS breakpoint:
 *   - desktop: three panels — Modelle (search + scrollable pill list) ·
 *     Variante (drawing pills) + Baujahr (spectrum pills) · drawing viewer
 *   - mobile: Modell / Variante / Baujahr as <select> fields above the viewer
 * Both representations are rendered and kept in sync; `.datte-ef-desktop` /
 * `.datte-ef-mobile` visibility lives in the SCSS.
 *
 * The viewer wraps the drawing in a ZoomController (shared component, same
 * controls as the category page). Markers are positioned as PERCENTAGES of
 * the image's natural size on a wrapper that exactly wraps the image, so
 * they stay glued to the drawing under zoom/pan transforms — no object-fit
 * math needed (the image is never letterboxed inside the wrapper).
 *
 * All user-visible strings come from the labels bag (JTL language system,
 * ErsatzteilSnippetLabels::FINDER_BAG); this module contains no hardcoded copy.
 */

import { ZoomController } from '../../../src-components/zoom/zoom-controller';
import type { FinderSelection, ModelEntry } from '../domain/selection';

/**
 * Presentation cleanup for the Baujahr axis: the spectrum's DB label with the
 * "Spectrum"/"Variante" filler words stripped — byte-identical to the legacy
 * finder's normalizeSpectrumName (product-attribute-description.ts). Display
 * concern only; the domain/wire layers carry the label untouched.
 */
export function normalizeSpectrumName(name: string): string {
  return name
    .replace(/Spectrum\s*/gi, '')
    .replace(/Variante\s*/gi, '')
    .trim();
}

/** UI strings, resolved server-side via Shop::Lang() and passed as data-labels. */
export interface FinderLabels {
  title: string;
  models: string;
  model: string;
  search: string;
  variant: string;
  year: string;
  drawing: string;
  tapHint: string;
  viewDevice: string;
}

/** Fallback = the key name, so a missing bag is visible, never German-in-TS. */
export function labelsFromJson(raw: string | null): FinderLabels {
  const keys: (keyof FinderLabels)[] = [
    'title',
    'models',
    'model',
    'search',
    'variant',
    'year',
    'drawing',
    'tapHint',
    'viewDevice',
  ];
  let parsed: Record<string, unknown> = {};
  if (raw) {
    try {
      parsed = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      parsed = {};
    }
  }
  const labels = {} as FinderLabels;
  for (const key of keys) {
    const value = parsed[key];
    labels[key] = typeof value === 'string' && value ? value : key;
  }
  return labels;
}

export interface FinderViewCallbacks {
  onSelectModel(index: number): void;
  onSelectSpectrum(index: number): void;
  onSelectDrawing(index: number): void;
  onSearch(): void;
}

export class FinderView {
  private zoom: ZoomController | null = null;
  private searchQuery = '';
  private renderedImageUrl: string | null = null;

  private readonly root: HTMLElement;
  private readonly searchInput: HTMLInputElement;
  private readonly modelList: HTMLUListElement;
  private readonly drawingPills: HTMLElement;
  private readonly spectrumGroup: HTMLElement;
  private readonly spectrumPills: HTMLElement;
  private readonly modelSelect: HTMLSelectElement;
  private readonly drawingSelect: HTMLSelectElement;
  private readonly spectrumField: HTMLElement;
  private readonly spectrumSelect: HTMLSelectElement;
  private readonly viewerTitle: HTMLElement;
  private readonly stage: HTMLElement;
  private readonly stageContent: HTMLElement;
  private readonly markerLayer: HTMLElement;
  private readonly image: HTMLImageElement;
  private readonly cta: HTMLAnchorElement;

  constructor(
    private readonly container: HTMLElement,
    private readonly labels: FinderLabels,
    private readonly callbacks: FinderViewCallbacks
  ) {
    this.container.innerHTML = this.skeleton();
    this.root = this.query('.datte-ef');
    this.searchInput = this.query('.datte-ef-search');
    this.modelList = this.query('.datte-ef-model-list');
    this.drawingPills = this.query('.datte-ef-drawing-pills');
    this.spectrumGroup = this.query('.datte-ef-group-spectrums');
    this.spectrumPills = this.query('.datte-ef-spectrum-pills');
    this.modelSelect = this.query('.datte-ef-model-select');
    this.drawingSelect = this.query('.datte-ef-drawing-select');
    this.spectrumField = this.query('.datte-ef-field-spectrum');
    this.spectrumSelect = this.query('.datte-ef-spectrum-select');
    this.viewerTitle = this.query('.datte-ef-viewer-title');
    this.stage = this.query('.datte-ef-stage');
    this.stageContent = this.query('.datte-ef-stage-content');
    this.markerLayer = this.query('.datte-ef-marker-layer');
    this.image = this.query('.datte-ef-image');
    this.cta = this.query('.datte-ef-cta');
    this.bindStaticEvents();
  }

  private query<T extends HTMLElement = HTMLElement>(selector: string): T {
    const el = this.container.querySelector<T>(selector);
    if (!el) throw new Error(`ersatzteil-finder: missing ${selector}`);
    return el;
  }

  private skeleton(): string {
    const l = this.labels;
    return `
<section class="datte-ef" aria-label="${escapeHtml(l.title)}">
  <h2 class="datte-ef-title">${escapeHtml(l.title)}</h2>
  <div class="datte-ef-layout">
    <div class="datte-ef-col datte-ef-col-models datte-ef-desktop">
      <h3 class="datte-ef-col-title" id="datte-ef-models-title">${escapeHtml(l.models)}</h3>
      <div class="datte-ef-models-box">
        <input type="search" class="datte-ef-search" placeholder="${escapeHtml(l.search)}" aria-label="${escapeHtml(l.search)}">
        <ul class="datte-ef-model-list" role="listbox" aria-labelledby="datte-ef-models-title"></ul>
      </div>
    </div>
    <div class="datte-ef-col datte-ef-col-axes datte-ef-desktop">
      <div class="datte-ef-group datte-ef-group-drawings">
        <h3 class="datte-ef-col-title">${escapeHtml(l.variant)}</h3>
        <div class="datte-ef-pills datte-ef-drawing-pills" role="group" aria-label="${escapeHtml(l.variant)}"></div>
      </div>
      <div class="datte-ef-group datte-ef-group-spectrums">
        <h3 class="datte-ef-col-title">${escapeHtml(l.year)}</h3>
        <div class="datte-ef-pills datte-ef-spectrum-pills" role="group" aria-label="${escapeHtml(l.year)}"></div>
      </div>
    </div>
    <div class="datte-ef-fields datte-ef-mobile">
      <label class="datte-ef-field">
        <span class="datte-ef-field-label">${escapeHtml(l.model)}</span>
        <select class="datte-ef-model-select"></select>
      </label>
      <label class="datte-ef-field">
        <span class="datte-ef-field-label">${escapeHtml(l.variant)}</span>
        <select class="datte-ef-drawing-select"></select>
      </label>
      <label class="datte-ef-field datte-ef-field-spectrum">
        <span class="datte-ef-field-label">${escapeHtml(l.year)}</span>
        <select class="datte-ef-spectrum-select"></select>
      </label>
    </div>
    <div class="datte-ef-col datte-ef-viewer">
      <h3 class="datte-ef-viewer-title"></h3>
      <p class="datte-ef-viewer-hint datte-ef-mobile">${escapeHtml(l.tapHint)}</p>
      <div class="datte-ef-stage">
        <div class="datte-ef-stage-content">
          <div class="datte-ef-marker-layer">
            <img class="datte-ef-image" alt="" draggable="false">
          </div>
        </div>
      </div>
      <div class="datte-ef-viewer-footer">
        <a class="datte-ef-cta" href="#">${escapeHtml(l.viewDevice)}</a>
      </div>
    </div>
  </div>
</section>`;
  }

  private bindStaticEvents(): void {
    this.searchInput.addEventListener('input', () => {
      this.searchQuery = this.searchInput.value;
      this.callbacks.onSearch();
    });
    this.modelList.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-index]');
      if (btn) this.callbacks.onSelectModel(Number(btn.dataset.index));
    });
    this.drawingPills.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-index]');
      if (btn) this.callbacks.onSelectDrawing(Number(btn.dataset.index));
    });
    this.spectrumPills.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-index]');
      if (btn) this.callbacks.onSelectSpectrum(Number(btn.dataset.index));
    });
    this.modelSelect.addEventListener('change', () =>
      this.callbacks.onSelectModel(Number(this.modelSelect.value))
    );
    this.drawingSelect.addEventListener('change', () =>
      this.callbacks.onSelectDrawing(Number(this.drawingSelect.value))
    );
    this.spectrumSelect.addEventListener('change', () =>
      this.callbacks.onSelectSpectrum(Number(this.spectrumSelect.value))
    );
  }

  /** Full sync of every axis + the viewer to the selection state. */
  render(selection: FinderSelection): void {
    this.renderModels(selection);
    this.renderSpectrums(selection);
    this.renderDrawings(selection);
    this.renderViewer(selection);
  }

  /** Re-render only the model list (search box input). */
  renderModelFilter(selection: FinderSelection): void {
    this.renderModels(selection);
  }

  private renderModels(selection: FinderSelection): void {
    const visible = selection.filterModels(this.searchQuery);
    const selectedIndex = selection.modelIdx;

    this.modelList.innerHTML = '';
    for (const entry of visible) {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'datte-ef-model';
      btn.dataset.index = String(entry.index);
      btn.setAttribute('role', 'option');
      btn.setAttribute('aria-selected', String(entry.index === selectedIndex));
      if (entry.index === selectedIndex) btn.classList.add('is-selected');
      const name = document.createElement('span');
      name.className = 'datte-ef-model-name';
      name.textContent = entry.label;
      const arrow = document.createElement('span');
      arrow.className = 'datte-ef-model-arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';
      btn.append(name, arrow);
      li.appendChild(btn);
      this.modelList.appendChild(li);
    }

    syncSelect(
      this.modelSelect,
      selection.models.map((entry: ModelEntry) => ({
        value: String(entry.index),
        label: entry.label,
      })),
      String(selectedIndex)
    );
  }

  private renderSpectrums(selection: FinderSelection): void {
    const show = selection.showSpectrumAxis;
    this.spectrumGroup.hidden = !show;
    this.spectrumField.hidden = !show;

    this.spectrumPills.innerHTML = '';
    const options: { value: string; label: string }[] = [];
    selection.spectrums.forEach((spectrum, i) => {
      const label = normalizeSpectrumName(spectrum.label);
      options.push({ value: String(i), label });
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'datte-ef-pill';
      btn.dataset.index = String(i);
      btn.textContent = label;
      btn.setAttribute('aria-pressed', String(i === selection.spectrumIdx));
      if (i === selection.spectrumIdx) btn.classList.add('is-selected');
      this.spectrumPills.appendChild(btn);
    });
    syncSelect(this.spectrumSelect, options, String(selection.spectrumIdx));
  }

  private renderDrawings(selection: FinderSelection): void {
    this.drawingPills.innerHTML = '';
    const options: { value: string; label: string }[] = [];
    selection.drawings.forEach((drawing, i) => {
      options.push({ value: String(i), label: drawing.name });
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'datte-ef-pill';
      btn.dataset.index = String(i);
      btn.textContent = drawing.name;
      btn.setAttribute('aria-pressed', String(i === selection.drawingIdx));
      if (i === selection.drawingIdx) btn.classList.add('is-selected');
      this.drawingPills.appendChild(btn);
    });
    syncSelect(this.drawingSelect, options, String(selection.drawingIdx));
  }

  private renderViewer(selection: FinderSelection): void {
    const model = selection.selectedModel;
    const drawing = selection.selectedDrawing;

    this.viewerTitle.textContent = `${model.manufacturer} ${model.model.device} ${model.model.name}: ${drawing.name}`;
    this.cta.href = selection.ctaUrl();

    // A fits-for model with no drawable IPL renders as a "device image": the
    // model's category thumbnail (or none), never any part pins. Style the
    // stage so the diagram affordances (zoom, "click a number") stay off.
    this.stage.classList.toggle('datte-ef-stage--device', drawing.isDeviceImage === true);

    if (this.renderedImageUrl !== drawing.imageUrl) {
      this.renderedImageUrl = drawing.imageUrl;
      this.zoom?.destroy();
      this.zoom = null;

      if (drawing.imageUrl === null) {
        // Imageless fits-for model: keep it a usable, clickable entry (title +
        // CTA); no broken <img>, no zoom.
        this.image.removeAttribute('src');
        this.image.alt = '';
        this.stage.classList.add('datte-ef-stage--imageless');
      } else {
        this.stage.classList.remove('datte-ef-stage--imageless');
        this.image.src = drawing.imageUrl;
        this.image.alt = `${model.label}: ${drawing.name}`;
        // Zoom is for exploded diagrams only — a device photo is not zoomable.
        if (drawing.isDeviceImage !== true) {
          const initZoom = () => {
            this.zoom?.destroy();
            this.zoom = new ZoomController({
              container: this.stage,
              content: this.stageContent,
              showControls: true,
              controlsPrefix: 'datte',
              showDiscoveryAnimation: false,
            });
          };
          if (this.image.complete && this.image.naturalWidth > 0) {
            initZoom();
          } else {
            this.image.addEventListener('load', initZoom, { once: true });
          }
        }
      }
    }

    this.placeMarkers(selection);
  }

  private placeMarkers(selection: FinderSelection): void {
    const drawing = selection.selectedDrawing;
    this.markerLayer.querySelectorAll('.datte-ef-marker').forEach((m) => m.remove());

    const place = () => {
      for (const marker of drawing.markers) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'datte-ef-marker';
        btn.textContent = marker.label;
        btn.title = marker.label;
        // marker.x/y are fractions [0,1] of the drawing frame (normalised at
        // the scraper). The marker layer overlays the image exactly, so the
        // fraction maps straight to a percentage — no division by the served
        // image's natural size (that overshot on downscaled drawings).
        btn.style.left = `${marker.x * 100}%`;
        btn.style.top = `${marker.y * 100}%`;
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          window.location.href = this.cta.href;
        });
        this.markerLayer.appendChild(btn);
      }
    };

    if (this.image.complete && this.image.naturalWidth > 0) {
      place();
    } else {
      this.image.addEventListener('load', place, { once: true });
    }
  }

  get currentSearchQuery(): string {
    return this.searchQuery;
  }
}

function syncSelect(
  select: HTMLSelectElement,
  options: { value: string; label: string }[],
  selectedValue: string
): void {
  select.innerHTML = '';
  for (const option of options) {
    const el = document.createElement('option');
    el.value = option.value;
    el.textContent = option.label;
    select.appendChild(el);
  }
  select.value = selectedValue;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
